"use client";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Loading } from "@/components/ui/loading";
import { useEffect, useRef, useState } from "react";

import { Tabs, TabsContent } from "@/components/ui/tabs";
import { PageHeader } from "@/components/common/PageHeader";

import { getQualificationType } from "../../applications/utils/qualificationUtils";
import { FileUploader } from "@/components/features/FileUploader";
import { useTranslation } from "react-i18next";
import { LeftSidebar, ProgressSection } from "./HelperComponents";
import {
  fetchTalentProfile,
  getProfilePictureUrl,
  getMockInterviewVideoUrl
} from "./utils";
import { useAuth } from "@/features/auth/context/AuthContext";
import { NotesAndComments } from "./NotesAndComments";
import { ApplicationOrCandidateTasks } from "./ApplicationOrCandidateTasks";

// Helper function to get badge color for German language level
const getGermanLevelBadgeColor = (level: string) => {
  switch (level.toLowerCase()) {
    case "a1":
    case "a2":
      return "bg-red-100 text-red-800 border-red-200"; // Basic levels - red
    case "b1":
      return "bg-yellow-100 text-yellow-800 border-yellow-200"; // Intermediate - yellow
    case "b2":
      return "bg-blue-100 text-blue-800 border-blue-200"; // Upper intermediate - blue
    case "c1":
      return "bg-green-100 text-green-800 border-green-200"; // Advanced - green
    case "c2":
      return "bg-emerald-100 text-emerald-800 border-emerald-200"; // Proficient - emerald
    default:
      return "bg-gray-100 text-gray-800 border-gray-200"; // Default - gray
  }
};

import {
  AdditionalQualificationsSection,
  AdditionalQualificationsView,
  InsuranceSection,
  InsuranceView,
  LanguageSkillsSection,
  LanguageSkillsView,
  DocumentsSection,
  PreparationCourseSection,
  PreparationCourseView,
  QualificationSection,
  QualificationView,
  TalentExperienceView,
  TalentEducationView,
  TalentInformation,
  VisaEntrySection,
  VisaEntryView,
  WorkContractSection,
  WorkContractView,
  WorkPermitSection,
  WorkPermitView
} from "../../applications/components";

interface CandidateProfileProps {
  id: string;
  breadcrumbs?: Array<{ title: string; href?: string }>;
}

export default function CandidateProfile({
  id,
  breadcrumbs
}: CandidateProfileProps) {
  const { t } = useTranslation(["applications", "common"]);
  const [pageData, setPageData] = useState<any>({
    given_name: "",
    family_name: "",
    email: "",
    phone: "",
    nationality: "",
    additional_languages: [], // Initialize as empty array
    theory_hours_deficit: null,
    practice_hours_deficit: null,
    residence_permit_type: "",
    nursing_education_status: "not_started",
    estimated_completion_date: null,
    education_location: "",
    education_address: "",
    education_street: "",
    education_street_number: "",
    education_postal_code: "",
    education_city: "",
    education_state: "",
    education_country: ""
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("general");
  const { profile: authProfile } = useAuth();
  const isSuperUser = authProfile?.isSuperUser;

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
  };

  const avatarInputRef = useRef<HTMLInputElement>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [mockInterviewVideoUrl, setMockInterviewVideoUrl] = useState<
    string | null
  >(null);

  // Section edit states
  const [sectionEditStates, setSectionEditStates] = useState<{
    [key: string]: boolean;
  }>({
    languages: false,
    qualification: false,
    additionalQualifications: false,
    visaEntry: false,
    insurance: false,
    workPermit: false,
    preparationCourse: false,
    workContract: false
  });

  // Helper functions for section completion
  const isSectionCompleted = (sectionKey: string): boolean => {
    switch (sectionKey) {
      case "languages":
        return !!(
          pageData.german_language_skills ||
          pageData.german_language_certificate ||
          (pageData.additional_languages &&
            pageData.additional_languages.length > 0)
        );
      case "qualification":
        return !!(
          pageData.education_type ||
          pageData.theory_hours_deficit !== null ||
          pageData.practice_hours_deficit !== null
        );
      case "additionalQualifications":
        return !!(
          pageData.work_experience ||
          pageData.advanced_training ||
          pageData.drivers_license ||
          pageData.german_drivers_license ||
          (pageData.nursing_education_status &&
            pageData.nursing_education_status !== "not_started")
        );
      case "visaEntry":
        return !!(pageData.residence_permit_type || pageData.visa_document);
      case "insurance":
        return !!pageData.health_insurance_status;
      case "workPermit":
        return !!pageData.work_permit_status;
      case "preparationCourse":
        return !!pageData.preparation_course_status;
      case "workContract":
        return !!pageData.contract_status;
      default:
        return false;
    }
  };

  const toggleSectionEdit = (sectionKey: string) => {
    setSectionEditStates((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  useEffect(() => {
    async function fetchData() {
      if (!id) {
        setLoading(false);
        return;
      }
      try {
        const profile = await fetchTalentProfile(id);
        setPageData(profile);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(
          err instanceof Error ? err.message : t("messages.error_occurred")
        );
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [id, t]);

  useEffect(() => {
    async function loadMediaUrls() {
      if (pageData.profile_picture) {
        const avatarUrl = await getProfilePictureUrl(pageData.profile_picture);
        setAvatarUrl(avatarUrl);
      }
      if (pageData.mock_interview_video) {
        try {
          const videoUrl = await getMockInterviewVideoUrl(
            pageData.mock_interview_video
          );
          setMockInterviewVideoUrl(videoUrl);
        } catch (e) {
          console.error("Error loading mock interview video", e);
        }
      } else {
        setMockInterviewVideoUrl(null);
      }
    }
    loadMediaUrls();
  }, [pageData.profile_picture, pageData.mock_interview_video]);

  if (loading) return <Loading text={t("messages.loading_profile")} />;

  if (error)
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-lg text-red-500">
          {t("messages.error")}: {error}
        </div>
      </div>
    );

  return (
    <div className="flex h-screen bg-white">
      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {breadcrumbs && <PageHeader breadcrumbs={breadcrumbs} />}

        {/* Profile Header */}
        <div className="p-6 border-b">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 md:gap-8">
            {/* Left side */}
            <div className="flex items-center gap-4">
              <div className="relative group">
                <Avatar
                  className="h-20 w-20 overflow-hidden"
                  videoUrl={mockInterviewVideoUrl}
                >
                  <AvatarImage
                    src={avatarUrl || undefined}
                    className="object-cover object-center"
                  />
                  <AvatarFallback>{`${pageData.given_name?.[0] || ""}${pageData.family_name?.[0] || ""}`}</AvatarFallback>
                </Avatar>
                <input
                  ref={avatarInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                />
              </div>
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-2xl font-bold">{`${pageData.given_name} ${pageData.family_name}`}</h1>
                </div>
                <div className="flex justify-betwee w-full">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge
                      variant="outline"
                      className="bg-gray-100 border-gray-200"
                    >
                      {getQualificationType(pageData)}
                    </Badge>

                    {/* German Language Level Badge */}
                    {pageData.german_language_skills && (
                      <Badge
                        variant="outline"
                        className={getGermanLevelBadgeColor(
                          pageData.german_language_skills
                        )}
                      >
                        German {pageData.german_language_skills.toUpperCase()}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
          <LeftSidebar
            application={pageData}
            onTabChange={handleTabChange}
            showJobTab={false}
            showActionsTab={true}
            canUploadMockInterview={isSuperUser}
            canViewCalendlyLink={isSuperUser}
            canViewNotesTab={isSuperUser}
            canViewTasksTab={isSuperUser}
          />

          {/* Main Content */}
          <div className="flex-1 overflow-auto">
            <div className="flex">
              {/* Form Content */}
              <div className="flex-1 p-4">
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  {/* Allgemein Tab */}
                  <TabsContent value="general" className="space-y-6">
                    <TalentInformation profile={pageData} />
                  </TabsContent>

                  {/* Experience Tab */}
                  <TabsContent value="experience" className="space-y-6">
                    <TalentExperienceView profile={pageData} />
                  </TabsContent>

                  {/* Education Tab */}
                  <TabsContent value="education" className="space-y-6">
                    <TalentEducationView profile={pageData} />
                  </TabsContent>

                  {/* Status & Fortschritt Tab */}
                  <TabsContent value="status" className="space-y-6">
                    <ProgressSection
                      title={t("common:german_skills")}
                      sectionKey="languages"
                      isCompleted={isSectionCompleted("languages")}
                      isEditing={sectionEditStates.languages}
                      onToggleEdit={() => toggleSectionEdit("languages")}
                      isNextSectionCompleted={isSectionCompleted(
                        "qualification"
                      )}
                      viewComponent={
                        <FileUploader>
                          <LanguageSkillsView profile={pageData} />
                        </FileUploader>
                      }
                    >
                      <FileUploader>
                        <LanguageSkillsSection
                          readOnly={true}
                          profile={pageData}
                          setProfile={setPageData}
                          loading={loading}
                        />
                      </FileUploader>
                    </ProgressSection>

                    <ProgressSection
                      title={t("sections.qualification.title")}
                      sectionKey="qualification"
                      isCompleted={isSectionCompleted("qualification")}
                      isEditing={sectionEditStates.qualification}
                      onToggleEdit={() => toggleSectionEdit("qualification")}
                      isNextSectionCompleted={isSectionCompleted(
                        "additionalQualifications"
                      )}
                      viewComponent={
                        <FileUploader>
                          <QualificationView profile={pageData} />
                        </FileUploader>
                      }
                    >
                      <FileUploader>
                        <QualificationSection
                          readOnly={true}
                          profile={pageData}
                          setProfile={setPageData}
                          loading={loading}
                        />
                      </FileUploader>
                    </ProgressSection>

                    <ProgressSection
                      title={t("sections.additional_qualifications.title")}
                      sectionKey="additionalQualifications"
                      isCompleted={isSectionCompleted(
                        "additionalQualifications"
                      )}
                      isEditing={sectionEditStates.additionalQualifications}
                      onToggleEdit={() =>
                        toggleSectionEdit("additionalQualifications")
                      }
                      isNextSectionCompleted={isSectionCompleted("visaEntry")}
                      viewComponent={
                        <AdditionalQualificationsView profile={pageData} />
                      }
                    >
                      <FileUploader>
                        <AdditionalQualificationsSection
                          readOnly={true}
                          profile={pageData}
                          setProfile={setPageData}
                          loading={loading}
                        />
                      </FileUploader>
                    </ProgressSection>

                    <ProgressSection
                      title={t("sections.visa_entry.title")}
                      sectionKey="visaEntry"
                      isCompleted={isSectionCompleted("visaEntry")}
                      isEditing={sectionEditStates.visaEntry}
                      onToggleEdit={() => toggleSectionEdit("visaEntry")}
                      isNextSectionCompleted={isSectionCompleted("insurance")}
                      viewComponent={
                        <FileUploader>
                          <VisaEntryView profile={pageData} />
                        </FileUploader>
                      }
                    >
                      <FileUploader>
                        <VisaEntrySection
                          readOnly={true}
                          profile={pageData}
                          setProfile={setPageData}
                          loading={loading}
                        />
                      </FileUploader>
                    </ProgressSection>

                    <ProgressSection
                      title={t("sections.insurance.title")}
                      sectionKey="insurance"
                      isCompleted={isSectionCompleted("insurance")}
                      isEditing={sectionEditStates.insurance}
                      onToggleEdit={() => toggleSectionEdit("insurance")}
                      isNextSectionCompleted={isSectionCompleted("workPermit")}
                      viewComponent={
                        <FileUploader>
                          <InsuranceView profile={pageData} />
                        </FileUploader>
                      }
                    >
                      <InsuranceSection
                        readOnly={true}
                        profile={pageData}
                        setProfile={setPageData}
                        loading={loading}
                      />
                    </ProgressSection>

                    <ProgressSection
                      title={t("sections.work_permit.title")}
                      sectionKey="workPermit"
                      isCompleted={isSectionCompleted("workPermit")}
                      isEditing={sectionEditStates.workPermit}
                      onToggleEdit={() => toggleSectionEdit("workPermit")}
                      isNextSectionCompleted={isSectionCompleted(
                        "preparationCourse"
                      )}
                      viewComponent={<WorkPermitView profile={pageData} />}
                    >
                      <FileUploader>
                        <WorkPermitSection
                          readOnly={true}
                          profile={pageData}
                          setProfile={setPageData}
                          loading={loading}
                        />
                      </FileUploader>
                    </ProgressSection>

                    <ProgressSection
                      title={t("sections.preparation_course.title")}
                      sectionKey="preparationCourse"
                      isCompleted={isSectionCompleted("preparationCourse")}
                      isEditing={sectionEditStates.preparationCourse}
                      onToggleEdit={() =>
                        toggleSectionEdit("preparationCourse")
                      }
                      isNextSectionCompleted={isSectionCompleted(
                        "workContract"
                      )}
                      viewComponent={
                        <PreparationCourseView profile={pageData} />
                      }
                    >
                      <PreparationCourseSection
                        readOnly={true}
                        profile={pageData}
                        setProfile={setPageData}
                        loading={loading}
                      />
                    </ProgressSection>

                    <ProgressSection
                      title={t("sections.work_contract.title")}
                      sectionKey="workContract"
                      isCompleted={isSectionCompleted("workContract")}
                      isEditing={sectionEditStates.workContract}
                      onToggleEdit={() => toggleSectionEdit("workContract")}
                      isLast={true}
                      viewComponent={<WorkContractView profile={pageData} />}
                    >
                      <FileUploader>
                        <WorkContractSection
                          readOnly={false}
                          profile={pageData}
                          setProfile={setPageData}
                          loading={loading}
                        />
                      </FileUploader>
                    </ProgressSection>
                  </TabsContent>
                  <TabsContent value="documents" className="space-y-6">
                    <FileUploader>
                      <DocumentsSection
                        isTalentProfile={true}
                        application_id={pageData.application_id}
                        talent_id={id}
                        canUploadDocuments={false}
                        canAssignRequiredDocuments={isSuperUser}
                        talentProfileData={pageData}
                        setTalentProfileData={setPageData}
                      />
                    </FileUploader>
                  </TabsContent>
                  <TabsContent value="notes" className="space-y-6">
                    <NotesAndComments
                      isTalentProfile={true}
                      talentOrApplicationId={id}
                    />
                  </TabsContent>
                  <TabsContent value="tasks" className="space-y-6">
                    <ApplicationOrCandidateTasks
                      isTalentProfile={true}
                      talentOrApplicationId={id}
                    />
                  </TabsContent>
                </Tabs>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
